// hooks/sale/useExportInvoiceSale.ts
import { useDispatch, useSelector } from 'react-redux';
import useAlerts from '../useAlerts';
import useLoading from '../useLoading';
import useModals from '../useModals';
import useConfirmModal from '../useConfirmModal';
import { RootState } from '../../store/store';

import { setExportInvoiceMetadata, updateSaleTotals, updateSaleTotalsView } from '../../store/slices/sale/saleSlice';
import { useSale } from './useSale';
import ExportInvoiceSaleService from '../../services/sale/ExportInvoiceSaleService';
import useApiErrorHandler from '../useApiErrorHandler';
import { closeAddProductModal, closeEditProductModal, setExportItemType } from '../../store/slices/sale/exportInvoiceSaleSlice';

export const useExportInvoiceSale = () => {
    const dispatch = useDispatch();
    const { show: showAlert } = useAlerts();
    const { openModal } = useModals();
    const confirm = useConfirmModal();
    const { handle } = useApiErrorHandler();
    const { showLoadingIndicator, hideLoadingIndicator } = useLoading();

    const {
        form,
        handlerAddProduct,
        handlerUpdateProduct,
        handlerDeleteProduct,
        handlerSelectProduct,
    } = useSale();

    const { showAddProductModal, showEditProductModal, exportItemType } = useSelector(
        (state: RootState) => state.exportInvoiceSale
    );

    const handlerAddExportInvoiceProduct = async (product: any) => {
        try {
            showLoadingIndicator();
            const response = await ExportInvoiceSaleService.addProduct(product);
            // console.log(response);

            if (response.statusCode === 201) {
                const { title, message, type, data } = response;
                const { product: savedProduct, totals, totalsView } = data;

                handlerAddProduct(savedProduct);
                dispatch(updateSaleTotals(totals));
                dispatch(updateSaleTotalsView(totalsView));
                handlerGetExportItemType();
                dispatch(closeAddProductModal());
                openModal(type, { title, message });
            } else {
                showAlert('error', 'Error', 'No se pudo agregar el producto.');
            }
        } catch (error: any) {
            handle(error);
        } finally {
            hideLoadingIndicator();
        }
    };

    const handlerUpdateExportInvoiceProduct = async (product: any) => {
        try {
            showLoadingIndicator();
            const response = await ExportInvoiceSaleService.updateProduct(product);
            console.log(response);


            if (response.statusCode === 200) {
                const { title, message, type, data } = response;
                const { product: updatedProduct, totals, totalsView } = data;

                handlerUpdateProduct(updatedProduct);
                dispatch(updateSaleTotals(totals));
                dispatch(updateSaleTotalsView(totalsView));
                handlerGetExportItemType();
                dispatch(closeEditProductModal());
                openModal(type, { title, message });
            } else {
                showAlert('error', 'Error', 'No se pudo actualizar el producto.');
            }
        } catch (error: any) {
            handle(error);
        } finally {
            hideLoadingIndicator();
        }
    };

    const handlerDeleteExportInvoiceProduct = async (id: number) => {
        confirm({
            title: '¿Eliminar Producto?',
            message: '¿Deseas eliminar este producto?',
            icon: 'warning',
            confirmText: 'Eliminar',
            cancelText: 'Cancelar',
            confirmArgs: id,
            onConfirm: async (id) => {
                try {
                    showLoadingIndicator();
                    const response = await ExportInvoiceSaleService.deleteProduct(id);
                    console.log(response);


                    if (response.statusCode === 200) {
                        const { title, message, type, data } = response;

                        handlerDeleteProduct(id);
                        dispatch(updateSaleTotals(data?.totals));
                        dispatch(updateSaleTotalsView(data?.totalsView));
                        dispatch(setExportInvoiceMetadata(data?.metadata));
                        handlerGetExportItemType();
                        openModal(type, { title, message });
                    } else {
                        showAlert('error', 'Error', 'No se pudo eliminar el producto.');
                    }
                } catch (error: any) {
                    handle(error);
                } finally {
                    hideLoadingIndicator();
                }
            },
        });
    };

    const handlerGetProductById = async (detailId: number) => {
        try {
            showLoadingIndicator();
            const response = await ExportInvoiceSaleService.getProductById(detailId);
            console.log(response);

            if (response.statusCode === 200) {
                handlerSelectProduct(response.data);
                return response.data;
            } else {
                showAlert('error', 'Error', 'No se pudo obtener el producto.');
            }
        } catch (error: any) {
            handle(error);
        } finally {
            hideLoadingIndicator();
        }
    };

    const handlerUpdateExportExtras = async (saleId: number, payload: any) => {
        console.log(saleId, payload)
        try {
            showLoadingIndicator();

            const response = await ExportInvoiceSaleService.updateExtras(saleId, payload);
            console.log('updateExtras response:', response);

            const { type, title, message, statusCode, data } = response;
            if (statusCode === 200) {
                showAlert(type, title, message);
                dispatch(updateSaleTotals(data?.totals));
                dispatch(updateSaleTotalsView(data?.totalsView));
            }

        } catch (error: any) {
            handle(error);
        } finally {
            hideLoadingIndicator();
        }
    };

    const handlerGetExportItemType = async () => {
        try {
            showLoadingIndicator();

            const response = await ExportInvoiceSaleService.getExportItemType(form?.id);
            const { data, statusCode } = response;

            if (statusCode === 200 && data?.exportItemType != null) {
                // console.log('exportItemType:', data.exportItemType);

                // 👇 guardamos SOLO el integer
                dispatch(setExportItemType(data.exportItemType));
            }
        } catch (error: any) {
            handle(error);
        } finally {
            hideLoadingIndicator();
        }
    };

    const handlerUpdateIncoterm = async (saleId: number, incotermId: number) => {
        try {
            showLoadingIndicator();

            const response = await ExportInvoiceSaleService.updateIncoterm(
                saleId,
                { incotermId }
            );

            const {
                statusCode,
                data,
                type,
                title,
                message
            } = response;

            if (statusCode === 200) {
                dispatch(setExportInvoiceMetadata(data));
                showAlert(type, title, message);
            }

        } catch (error: any) {
            handle(error);
        } finally {
            hideLoadingIndicator();
        }
    };


    const handlerUpdateExportRegime = async (
        saleId: number,
        exportRegimeId: number
    ) => {
        try {
            showLoadingIndicator();

            const response = await ExportInvoiceSaleService.updateExportRegime(
                saleId,
                { exportRegimeId }
            );

            const {
                statusCode,
                data,
                type,
                title,
                message
            } = response;

            if (statusCode === 200) {
                dispatch(setExportInvoiceMetadata(data));
                showAlert(type, title, message);
            }

        } catch (error: any) {
            handle(error);
        } finally {
            hideLoadingIndicator();
        }
    };



    const handlerUpdateFiscalPrecinct = async (
        saleId: number,
        fiscalPrecinctId: number
    ) => {
        try {
            showLoadingIndicator();

            const response = await ExportInvoiceSaleService.updateFiscalPrecinct(
                saleId,
                { fiscalPrecinctId }
            );

            const {
                statusCode,
                data,
                type,
                title,
                message
            } = response;

            if (statusCode === 200) {
                dispatch(setExportInvoiceMetadata(data));
                showAlert(type, title, message);
            }

        } catch (error: any) {
            handle(error);
        } finally {
            hideLoadingIndicator();
        }
    };


    return {
        handlerAddExportInvoiceProduct,
        handlerUpdateExportInvoiceProduct,
        handlerDeleteExportInvoiceProduct,
        handlerGetProductById,

        handlerUpdateExportExtras,
        handlerGetExportItemType,

        handlerUpdateIncoterm,
        handlerUpdateExportRegime,
        handlerUpdateFiscalPrecinct,

        exportItemType,
        showAddProductModal,
        showEditProductModal,
    };
};
